import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { requestJump } from '../shared/spotlight';
import { alphabetical, useDefaultProjectOrder } from '../shared/order';
import { data, useData, useSetting, useTable } from '@core/store';
import { WriteOnly } from '@core/auth/access';
import { DEFAULT_PROJECT_COLOR } from '@core/palette';
import { mondayOf, today } from '@core/dates';
import { SegmentedControl } from '@core/ui/SegmentedControl';
import { Button } from '@core/ui/Button';
import { ScreenLoading } from '@core/ui/Loading';
import { applyPlanned } from '../shared/apply';
import { RangeControls } from '../shared/RangeControls';
import { RELEASE_PRESETS, defaultReleaseRange, type Range } from '../shared/range';
import { ScreenToolbar } from '../shared/Toolbar';
import { control } from '@core/ui/form';
import { TaskModal } from '../shared/TaskModal';
import { useTaskCard, useToast } from '../shared/interaction';
import { useScrollMemory } from '@core/ui/scrollMemory';
import { rowKeyOf, rowTasksNow, taskNow, useAssignees, useAttention, useRoadmapTasks, type Task } from '../shared/data';
import { askEarlyDeploy } from '../shared/deployGuard';
import { EventModal, eventDraftOf, type EventDraft } from '../shared/EventModal';
import { STATUS_LOOK, launchIdOfTrack, type LaunchTask } from '../../launch/planner';
import { useLaunchTaskCard } from '../../launch/useLaunchTaskCard';
import { LAUNCH_CHIP, eventsInRange, groupByMonth, lanesOf, matchesFilter, planDeployWeek, planShiftToWeek, releaseOrder, releaseWeekOf, releasesOf, timelineCount, type Release } from './planner';
import { ProjectChips } from './ProjectChips';
import { WeeksView } from './WeeksView';
import { Timeline } from './Timeline';
import { LanesModal } from './LanesModal';
import type { CardActions, Person } from './ReleaseCard';

type View = 'weeks' | 'timeline';
const VIEWS: { key: View; label: string }[] = [{ key: 'weeks', label: 'Weeks' }, { key: 'timeline', label: 'Timeline' }];

/** Release Plan: что и когда выходит. Экран чтения для бизнеса; правки — те же, что в Projects. */
export function ReleaseScreen() {
  const status = useData((s) => s.status);
  const [view, setView] = useSetting<View>('release.view', 'weeks');
  const bodyRef = useRef<HTMLDivElement>(null);
  useScrollMemory(bodyRef, `release:${view}`, status === 'ready'); // где стоял в Weeks и на Timeline — на время сессии
  const [range, setRange] = useSetting<Range>('release.range', defaultReleaseRange());
  const [query, setQuery] = useState(''); // поиск — состояние экрана: хранить его посимвольно незачем
  const [projectFilter, setProjectFilter] = useState<string[]>([]); // временный фильтр: живёт пока открыта вкладка
  const [lanesOpen, setLanesOpen] = useState(false);
  const [eventDraft, setEventDraft] = useState<EventDraft | null>(null);
  const laneRows = useTable('timeline_lane');
  const laneLinks = useTable('lane_project');
  const eventRows = useTable('event');
  const attention = useAttention();
  const { allTasks } = useRoadmapTasks();
  const launchTasks = useTable('launch_task');
  const launches = useTable('launch');
  const launchTracks = useTable('launch_track');
  const launchSwimlanes = useTable('launch_swimlane');
  const projects = useTable('project');
  const people = useTable('person');
  const assigneeRows = useTable('task_assignee');
  const { names } = useAssignees();
  const { fail } = useToast();
  const { draft, openTask, closeModal, cardTab } = useTaskCard();
  const navigate = useNavigate();
  useEffect(() => { data.updatePresence({ screen: 'release plan' }); }, []);

  const projectList = useMemo(() => alphabetical(projects.values()), [projects]);
  const defaultOrder = useDefaultProjectOrder();
  const filter = useMemo(() => ({ query, projects: new Set(projectFilter) }), [query, projectFilter]);
  // экран общий: карточки внутри недели — ключевые первыми, потом по общему порядку проектов по умолчанию
  const order = useMemo(() => releaseOrder(new Map(defaultOrder.map((id, i) => [id, i]))), [defaultOrder]);
  const launchOf = useCallback((t: LaunchTask) => { const id = launchIdOfTrack(t.track_id, launchTracks, launchSwimlanes); return id ? launches.get(id) : undefined; }, [launchTracks, launchSwimlanes, launches]);
  const all = useMemo(() => releasesOf(allTasks.values(), launchTasks.values(), launchOf), [allTasks, launchTasks, launchOf]);
  const releases = useMemo(() => all.filter((r) => matchesFilter(r, names.get(r.id) ?? [], filter)).sort(order), [all, names, filter, order]);
  // чип Launch — только когда релизы лонча вообще есть; цвет статуса release
  const chips = useMemo(() => (all.some((r) => r.kind === 'launch') ? [...projectList, { id: LAUNCH_CHIP, name: 'Launch', color: STATUS_LOOK.release.border }] : projectList), [all, projectList]);
  const months = useMemo(() => groupByMonth(releases, range), [releases, range]);
  const lanes = useMemo(() => lanesOf(laneRows.values(), laneLinks.values()), [laneRows, laneLinks]);
  const events = useMemo(() => eventsInRange(eventRows.values(), range), [eventRows, range]);
  const inRange = useMemo(() => releases.filter((r) => r.week >= mondayOf(range.from) && r.week <= range.to), [releases, range]);
  // карточка задачи лонча — та же, что на Launch Board, с данными его доски
  const launchCard = useLaunchTaskCard();
  const openRelease = useCallback((r: Release) => (r.kind === 'task' ? openTask(r.task) : launchCard.open(r.task)), [openTask, launchCard]);
  // Timeline показывает только проекты с лентой — счётчик в тулбаре считает то, что видно
  const visibleCount = view === 'timeline' ? timelineCount(lanes, inRange) : months.reduce((n, m) => n + m.count, 0);

  const projectOf = useCallback((t: Task) => { const p = projects.get(t.project_id); return { name: p?.name ?? '—', color: p?.color ?? DEFAULT_PROJECT_COLOR }; }, [projects]);
  // исполнители по задаче один раз на изменение таблиц, не на каждую карточку
  const peopleByTask = useMemo(() => {
    const out = new Map<string, Person[]>();
    for (const a of [...assigneeRows.values()].sort((a, b) => a.position - b.position)) {
      const p = people.get(a.person_id);
      if (p) out.set(a.task_id, [...(out.get(a.task_id) ?? []), { name: p.name, color: p.color }]);
    }
    return out;
  }, [assigneeRows, people]);
  const peopleOf = useCallback((t: Task): Person[] => peopleByTask.get(t.id) ?? [], [peopleByTask]);

  // действия карточки — по подтверждённой строке, одним полем; expect_updated_at — чужая правка даёт conflict, не перезапись
  const setField = useCallback((task: Task, kind: string, set: (t: Task) => Record<string, unknown>) => {
    void applyPlanned(() => { const t = taskNow(task.id) ?? task; return { changes: [{ table: 'task', id: t.id, set: set(t), expect_updated_at: t.updated_at }], op: { kind, task_id: t.id } }; })
      .then((r) => { if (!r.ok) fail(r.error); });
  }, [fail]);
  const actions: CardActions = useMemo(() => ({
    confirm: (t) => setField(t, 'release_confidence', (x) => ({ release_confidence: x.release_confidence === 'confirmed' ? null : 'confirmed' })),
    atRisk: (t) => setField(t, 'release_confidence', (x) => ({ release_confidence: x.release_confidence === 'at_risk' ? null : 'at_risk' })),
    star: (t) => setField(t, 'key_release', (x) => ({ key_release: !x.key_release })),
    drop: (t) => setField(t, 'not_deploying', () => ({ deploy: 'none', deploy_date: null })),
    open: (t, tab) => openTask(t, tab),
    jump: (t) => { requestJump(t.id); void navigate('/roadmap/projects'); },
  }), [setField, openTask, navigate]);

  // чип или карточку на другую неделю: прибитая дата на понедельник той недели. Раньше конца работы —
  // окно: сдвинуть работу так, чтобы она кончилась в ту неделю (тот же planMove, что на сетке), или отмена.
  // Релиз лонча не тянется (Глеб): его дата меняется на Launch Board или в карточке.
  const onDropWeek = useCallback(async (release: Release, monday: string) => {
    if (release.kind === 'launch') return;
    const t = taskNow(release.id) ?? release.task;
    if (releaseWeekOf(t) === monday) return; // бросили в свою же неделю — ничего не пишем
    const plan = planDeployWeek(t, monday);
    if ('error' in plan) {
      const choice = await askEarlyDeploy({ task: t, monday });
      if (choice !== 'shift') return;
      const r = await applyPlanned(() => { const live = taskNow(t.id) ?? t; return planShiftToWeek(live, monday, rowTasksNow(rowKeyOf(live.project_id, live.subproject_id))); });
      if (!r.ok) fail(r.error);
      return;
    }
    const r = await applyPlanned(() => { const live = taskNow(t.id) ?? t; return { changes: [{ table: 'task', id: live.id, set: plan.set, expect_updated_at: live.updated_at }], op: { kind: 'set_deploy_week', task_id: live.id } }; });
    if (!r.ok) fail(r.error);
  }, [fail]);

  if (status !== 'ready') return <ScreenLoading />;

  const filtered = query.trim() !== '' || projectFilter.length > 0;
  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow border border-gray-300 overflow-hidden">
      <ScreenToolbar
        actions={view === 'timeline' && <WriteOnly><Button variant="ghost" size="sm" onClick={() => setLanesOpen(true)} title="Lanes: name, projects, what they show">Edit lanes</Button></WriteOnly>}
        what={<SegmentedControl role="tabs" label="Release Plan view" items={VIEWS} value={view} onChange={setView} />}
        when={<RangeControls range={range} onChange={setRange} onReset={() => setRange(null)} presets={RELEASE_PRESETS} def={defaultReleaseRange()} />}
        filter={<input
          type="search" className={`${control} w-56`} placeholder="Search releases…" aria-label="Search releases"
          value={query} onChange={(e) => setQuery(e.target.value)}
        />}
        status={<span className="text-sm text-gray-600 whitespace-nowrap" data-release-count={visibleCount}>
          <span className="font-semibold text-gray-900">{visibleCount}</span> {visibleCount === 1 ? 'release' : 'releases'}{filtered ? ' shown' : ''}
        </span>}
      />
      <ProjectChips projects={chips} selected={projectFilter} onChange={setProjectFilter} filtered={filtered} onClear={() => { setQuery(''); setProjectFilter([]); }} />
      <div ref={bodyRef} className="flex-1 overflow-y-auto scroller p-4 bg-gray-100" data-release-body>
        {view === 'weeks' && <WeeksView months={months} today={today()} projectOf={projectOf} peopleOf={peopleOf} attention={attention} actions={actions} onOpen={openRelease} onDrop={(r, monday) => void onDropWeek(r, monday)} />}
        {view === 'timeline' && (
          <Timeline lanes={lanes} releases={inRange} events={events} range={range} today={today()} projectOf={projectOf} peopleOf={peopleOf} attention={attention} actions={actions}
            onOpen={openRelease} onOpenEvent={(e) => setEventDraft(eventDraftOf(e))} onDrop={(r, monday) => void onDropWeek(r, monday)} />
        )}
        {view === 'weeks' && visibleCount === 0 && <p className="text-ink-muted mt-4">{filtered ? 'Nothing matches the filter.' : 'No releases in this range.'}</p>}
      </div>
      {draft && <TaskModal draft={draft} allTasks={allTasks} onClose={closeModal} initialTab={cardTab} />}
      {launchCard.node}
      {eventDraft && <EventModal draft={eventDraft} onClose={() => setEventDraft(null)} />}
      {lanesOpen && <LanesModal onClose={() => setLanesOpen(false)} />}
    </div>
  );
}
